import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@opswat/ui';
import { Link } from 'components/Link';

import { FOOTER_SOCIAL_LINKS } from './Footer.data';

export type FooterIconName = ComponentProps<typeof Icon>['iconName'];

export type FooterSocialLink = {
    labelKey: string;
    href: string;
} & ({ iconName: FooterIconName; imgSrc?: never } | { iconName?: never; imgSrc: string });

export const Footer = () => {
    const { t } = useTranslation();
    const currentYear = new Date(Date.now()).getFullYear();

    return (
        <footer className='tw:w-full tw:bg-neutrals-200 tw:dark:bg-neutrals-1100'>
            <div className='tw:border-b tw:border-neutrals-300 tw:dark:border-neutrals-900 tw:h-120'>
                <div className='tw:mx-auto tw:px-20 tw:flex tw:items-center tw:justify-between tw:h-full tw:max-w-1300'>
                    <Link
                        to='/'
                        className='tw:no-underline hover:tw:no-underline focus-visible:tw:outline focus-visible:tw:outline-1 focus-visible:tw:outline-neutrals-700 tw:dark:focus-visible:tw:outline-white tw:rounded-sm'
                    >
                        <div className='tw:flex tw:flex-col tw:gap-2 tw:text-neutrals-1200 tw:dark:text-white'>
                            <div className='tw:text-footnote tw:font-medium tw:uppercase tw:tracking-wider'>
                                {t('METADEFENDER')}
                            </div>
                            <div className='tw:text-headline-m tw:font-medium'>{t('Cloud')}</div>
                        </div>
                    </Link>
                    <div className='tw:flex tw:items-center tw:gap-20'>
                        {FOOTER_SOCIAL_LINKS.map((link) => (
                            <Link
                                key={link.href}
                                to={link.href}
                                isExternal
                                aria-label={t(link.labelKey)}
                                className='tw:relative tw:inline-flex tw:items-center tw:justify-center tw:text-neutrals-1200 tw:dark:text-white hover:tw:text-neutrals-1200 tw:dark:hover:text-white tw:rounded-full focus-visible:tw:outline focus-visible:tw:outline-1 focus-visible:tw:outline-neutrals-700 tw:dark:focus-visible:tw:outline-white'
                            >
                                {link.iconName ? (
                                    <Icon iconName={link.iconName} className='tw:size-20' />
                                ) : (
                                    <img
                                        src={link.imgSrc}
                                        alt=''
                                        aria-hidden='true'
                                        className='tw:size-20 tw:dark:invert'
                                    />
                                )}
                                <span className='tw:sr-only'>{t(link.labelKey)}</span>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
            <div className='tw:py-20'>
                <div className='tw:mx-auto tw:px-20 tw:flex tw:items-center tw:justify-between tw:max-w-1300'>
                    <span className='tw:text-note tw:text-neutrals-600 tw:dark:text-neutrals-500'>
                        {t('©{{year}} OPSWAT, Inc. All rights reserved.', { year: currentYear })}
                    </span>
                    <div className='tw:flex tw:flex-wrap tw:items-center tw:justify-end tw:gap-20'>
                        <Link
                            to='https://www.opswat.com/legal/terms-of-service'
                            isExternal
                            className='tw:text-note tw:text-neutrals-700 tw:dark:text-neutrals-500 tw:no-underline hover:tw:no-underline hover:tw:text-neutrals-1200 tw:dark:hover:text-white'
                        >
                            {t('Terms of Service')}
                        </Link>
                        <Link
                            to='https://www.opswat.com/legal/privacy-policy'
                            isExternal
                            className='tw:text-note tw:text-neutrals-700 tw:dark:text-neutrals-500 tw:no-underline hover:tw:no-underline hover:tw:text-neutrals-1200 tw:dark:hover:text-white'
                        >
                            {t('Privacy Policy')}
                        </Link>
                        <Link
                            to='https://www.opswat.com/company/patents'
                            isExternal
                            className='tw:text-note tw:text-neutrals-700 tw:dark:text-neutrals-500 tw:no-underline hover:tw:no-underline hover:tw:text-neutrals-1200 tw:dark:hover:text-white'
                        >
                            {t('OPSWAT Patent Information')}
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};
